import classNames from "classnames";
import {
  action,
  computed,
  IComputedValue,
  IObservableValue,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { observer } from "mobx-react-lite";
import {
  ChatCompletionRequestMessage,
  ChatCompletionResponseMessage,
  CreateChatCompletionRequest,
  CreateChatCompletionResponse,
  CreateCompletionRequest,
  CreateCompletionResponse,
} from "openai";
import React, { useEffect, useRef, useState } from "react";
import { atom } from "../atom";
import { LLM_OPENAI_CONFIG_DEFAULT_VALUES } from "../Constants";
import {
  ActionType,
  EnvironmentVariableValue,
  OPENAI_MODELS,
  Pane,
  PaneType,
  PaneViewMode,
} from "../Types";
import {
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
} from "../ui/Dialog";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "../ui/Menu";
import { SecretValue } from "../ui/SecretValue";
import { SelectItem, SelectRoot } from "../ui/Select";
import { TextInput } from "../ui/TextInput";
import { Tooltip } from "../ui/Tooltip";
import { assert, throttle } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasLayoutIdAtom,
  keyboardTransformPaneIdBox,
} from "./CanvasState";
import { getEnvironmentVariableAtom } from "./EnvironmentVariable";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import { JSONRenderRoot } from "./JSONRender";
import { PaneBody } from "./PaneBody";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import { PaneInputNode, PopupPaneInputId } from "./PaneInput";
import { isCustomLayoutId } from "./PaneLayout";
import { PaneSplit } from "./PaneSplit";
import { NumberRangeInput } from "./PaneStateType";
import { isLLMConfigChat } from "./SandboxUtils";

function isValidChatInput(input: any) {
  return (
    Array.isArray(input) &&
    input.every(
      (item) =>
        typeof item.role === "string" && typeof item.content === "string"
    )
  );
}

const OpenAIModelSelect = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.LLM }> | undefined
    >;
  }) => {
    const model = computed(() => paneComputed.get()?.config.model).get();
    return (
      <SelectRoot
        value={model ?? ""}
        onChange={(value) => {
          canvasDispatch([
            ActionType.SetPaneLLMConfig,
            { paneId, key: "model", value },
          ]);
        }}
      >
        {OPENAI_MODELS.map((model) => (
          <SelectItem text={model} value={model} key={model} />
        ))}
      </SelectRoot>
    );
  }
);

function parseStopText(input: string): string[] | undefined {
  try {
    const value = JSON.parse(input);
    if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
      return value;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

const LLMConfigStopInput = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.LLM }> | undefined
    >;
  }) => {
    const [value, setValue] = useState(() => {
      const pane = paneComputed.get();
      return pane?.config.stop !== undefined
        ? JSON.stringify(pane.config.stop)
        : "";
    });
    const stopValue = parseStopText(value);
    const [throttleDispatch] = useState(() =>
      throttle((value: string[] | undefined) => {
        canvasDispatch([
          ActionType.SetPaneLLMConfig,
          { paneId, key: "stop", value },
        ]);
      }, 300)
    );
    useEffect(() => () => throttleDispatch.cancel(), []);
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(
      () =>
        reaction(
          () => paneComputed.get()?.config.stop,
          (value) => {
            if (document.activeElement !== inputRef.current) {
              setValue(value === undefined ? "" : JSON.stringify(value));
            }
          }
        ),
      []
    );

    return (
      <input
        type="text"
        placeholder={`["\\n\\n"]`}
        value={value}
        onChange={(e) => {
          const textValue = e.target.value;
          setValue(textValue);
          if (textValue.trim() === "") {
            throttleDispatch(undefined);
          } else {
            const stopValue = parseStopText(textValue);
            if (stopValue !== undefined) {
              throttleDispatch(stopValue);
            }
          }
        }}
        onBlur={() => {
          const stop = paneComputed.get()?.config.stop;
          setValue(stop !== undefined ? JSON.stringify(stop) : "");
        }}
        className={classNames(
          "font-mono-natto border border-neutral-200 hover:border-primary-400 focus:border-primary-400 px-2 py-1 rounded-sm transition dark:bg-neutral-150",
          stopValue === undefined && value.trim() !== ""
            ? "bg-red-50"
            : undefined
        )}
        ref={inputRef}
      />
    );
  }
);

const LLMConfigNumberInput = observer(
  ({
    paneId,
    min,
    max,
    step,
    configKey,
    paneComputed,
  }: {
    paneId: string;
    min: number;
    max: number;
    step: number;
    configKey: "temperature" | "max_tokens";
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.LLM }> | undefined
    >;
  }) => {
    const [valueBox] = useState(() =>
      observable.box<["value", number]>([
        "value",
        paneComputed.get()?.config[configKey] ??
          LLM_OPENAI_CONFIG_DEFAULT_VALUES[configKey],
      ])
    );
    useEffect(
      () =>
        reaction(
          () => valueBox.get(),
          (valueOutput) => {
            const value = valueOutput[1];
            canvasDispatch([
              ActionType.SetPaneLLMConfig,
              { paneId, key: configKey, value },
            ]);
          }
        ),
      []
    );
    useEffect(
      () =>
        reaction(
          () => paneComputed.get()?.config[configKey],
          action((value) => {
            if (valueBox.get()[1] !== value) {
              valueBox.set(["value", value ?? 1]);
            }
          })
        ),
      []
    );
    return (
      <div className="w-full">
        <NumberRangeInput
          min={min}
          max={max}
          step={step}
          outputBox={valueBox}
        />
      </div>
    );
  }
);

function LLMConfigFormRow({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center">
      <label className="flex items-center gap-1 font-medium text-neutral-600 w-1/3 min-w-[6rem]">
        {label}
        {tooltip !== undefined ? (
          <Tooltip content={tooltip}>
            <FeatherIcon
              icon="help-circle"
              size="sm"
              className="text-neutral-400"
            />
          </Tooltip>
        ) : null}
      </label>
      <div className="grow flex justify-end">{children}</div>
    </div>
  );
}

const LLMConfigForm = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.LLM }> | undefined
    >;
  }) => {
    return (
      <div className="flex flex-col gap-1 p-2">
        <LLMConfigFormRow label="provider">
          <div className="py-1">OpenAI</div>
        </LLMConfigFormRow>
        <LLMConfigFormRow label="model">
          <OpenAIModelSelect paneId={paneId} paneComputed={paneComputed} />
        </LLMConfigFormRow>
        <LLMConfigFormRow label="temperature">
          <LLMConfigNumberInput
            paneId={paneId}
            min={0}
            max={1}
            step={0.01}
            configKey="temperature"
            paneComputed={paneComputed}
          />
        </LLMConfigFormRow>
        <LLMConfigFormRow label="max tokens">
          <LLMConfigNumberInput
            paneId={paneId}
            min={1}
            max={4000}
            step={1}
            configKey="max_tokens"
            paneComputed={paneComputed}
          />
        </LLMConfigFormRow>
        <LLMConfigFormRow
          label="stop"
          tooltip={"json array of strings\n(better UI in future)"}
        >
          <LLMConfigStopInput paneId={paneId} paneComputed={paneComputed} />
        </LLMConfigFormRow>
      </div>
    );
  }
);

const sessionOpenAIAPIKeyBox = observable.box<string | undefined>(undefined);
const OPENAI_API_KEY_NAME = "natto.OPENAI_API_KEY";

const OpenAISetupDialogContent = observer(() => {
  const sessionAPIKey = sessionOpenAIAPIKeyBox.get();
  const apiKeyInputRef = useRef<HTMLInputElement | null>(null);

  const apiKeyEnvironmentVariable =
    getEnvironmentVariableAtom(OPENAI_API_KEY_NAME).value;

  return (
    <div className="overflow-auto pb-8">
      <DialogHeader>LLM pane setup</DialogHeader>
      <div className="px-6 pt-6">
        <div className="mb-4">
          The LLM pane requires an{" "}
          <a
            href="https://beta.openai.com/account/api-keys"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary-500 hover:underline"
          >
            OpenAI API key
          </a>
          . You can either set a key for only this session or use a persisted
          environment variable.
        </div>
        <div className="border-b border-neutral-100 pb-4 mb-4">
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="api-key-input" className="flex gap-1 items-center">
              <span className="font-medium">OpenAI API Key for session</span>
              <Tooltip content={"not persisted\ncleared on navigation"}>
                <FeatherIcon
                  icon="help-circle"
                  size="sm"
                  className="text-neutral-400"
                />
              </Tooltip>
            </label>
          </div>
          <TextInput
            id="api-key-input"
            value={sessionAPIKey ?? ""}
            onChange={action((e) => {
              const value = e.target.value;
              if (value.trim() === "") {
                sessionOpenAIAPIKeyBox.set(undefined);
              } else {
                sessionOpenAIAPIKeyBox.set(value.trim());
              }
            })}
            placeholder="sk-g9dcx4rwBODX2UkD..."
            className="w-full"
            ref={apiKeyInputRef}
          />
        </div>
        <div>
          <div className="mb-4">
            If there is no session key set, natto will use the environment
            variable <code className="font-medium">{OPENAI_API_KEY_NAME}</code>{" "}
            if defined.
          </div>
          {apiKeyEnvironmentVariable[0] === "unauthorized" ? (
            <div>
              <div className="mb-2">
                This canvas does not have access to your environment variables.
              </div>
              <button
                onClick={() => {
                  ParentComlinkAPI.grantEnvironment();
                }}
                className="button"
              >
                authorize
              </button>
            </div>
          ) : (
            <div>
              {apiKeyEnvironmentVariable[0] === "value" ? (
                <div>
                  <div className="mb-4">
                    <div className="font-mono-natto text-2xs mb-1">
                      {OPENAI_API_KEY_NAME}
                    </div>
                    <div className="whitespace-nowrap">
                      <SecretValue value={apiKeyEnvironmentVariable[1]} />
                    </div>
                  </div>
                  <div>
                    <button
                      onClick={() => {
                        ParentComlinkAPI.showEnvironmentDialog(
                          OPENAI_API_KEY_NAME
                        );
                      }}
                      className="button"
                    >
                      manage environment variables
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <button
                    onClick={() => {
                      ParentComlinkAPI.showEnvironmentDialog(
                        OPENAI_API_KEY_NAME
                      );
                    }}
                    className="button"
                  >
                    define environment variable
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const showOpenAISetupDialogAtom = atom(false);
export const OpenAISetupDialogController = observer(() => {
  return (
    <DialogRoot
      open={showOpenAISetupDialogAtom.value}
      onOpenChange={action((open) => {
        if (!open) {
          showOpenAISetupDialogAtom.value = false;
        }
      })}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="w-[90vw] max-w-sm">
          <OpenAISetupDialogContent />
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
});

type LLMPaneState =
  | { type: "idle" }
  | {
      type: "running";
      mode: "prompt";
      request: CreateCompletionRequest;
      pendingTextBox: IObservableValue<string>;
    }
  | {
      type: "running";
      mode: "chat";
      request: CreateChatCompletionRequest;
      pendingTextBox: IObservableValue<string>;
    }
  | {
      type: "value";
      mode: "prompt";
      request: CreateCompletionRequest;
      response: CreateCompletionResponse;
    }
  | {
      type: "value";
      mode: "chat";
      request: CreateChatCompletionRequest;
      response: CreateChatCompletionResponse;
    }
  | {
      type: "error";
      mode: "prompt";
      request: CreateCompletionRequest;
      error: any;
    }
  | {
      type: "error";
      mode: "chat";
      request: CreateChatCompletionRequest;
      error: any;
    };

const LLMRunBar = observer(
  ({
    paneComputed,
    llmStateBox,
    inputValueBox,
    apiKey,
    isChatMode,
    isInputValueValid,
  }: {
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.LLM }> | undefined
    >;
    llmStateBox: IObservableValue<LLMPaneState>;
    inputValueBox: IComputedValue<any | undefined>;
    apiKey: EnvironmentVariableValue;
    isChatMode: boolean;
    isInputValueValid: boolean;
  }) => {
    const isRunning = computed(
      () => llmStateBox.get().type === "running"
    ).get();
    const tokensUsed = computed(() => {
      const llmState = llmStateBox.get();
      if (llmState.type !== "value") {
        return undefined;
      }
      const { usage } = llmState.response;
      return usage !== undefined
        ? [usage.prompt_tokens, usage.completion_tokens, usage.total_tokens]
        : undefined;
    }).get();

    return (
      <div className="flex gap-3 items-center px-2 py-1.5 border-b border-neutral-150">
        <Tooltip
          content={
            apiKey[0] !== "value"
              ? "setup required"
              : !isInputValueValid
              ? isChatMode
                ? "messages input is\nnot a valid array"
                : "prompt input is not a string"
              : null
          }
          disabled={apiKey[0] === "value" && isInputValueValid}
        >
          <button
            onClick={async () => {
              const pane = paneComputed.get();
              if (pane === undefined) {
                return;
              }
              if (apiKey[0] !== "value") {
                return;
              }
              const llmConfig = pane.config;
              const mode = isLLMConfigChat(llmConfig) ? "chat" : "prompt";
              let llmState: Extract<LLMPaneState, { type: "running" }>;
              const pendingTextBox = observable.box("");
              if (mode === "chat") {
                llmState = {
                  type: "running",
                  mode: "chat",
                  request: {
                    model: llmConfig.model,
                    messages: inputValueBox.get(),
                    max_tokens: llmConfig.max_tokens,
                    temperature: llmConfig.temperature,
                    top_p: undefined,
                    n: undefined,
                    stream: true,
                    stop: llmConfig.stop,
                    presence_penalty: undefined,
                    frequency_penalty: undefined,
                    logit_bias: undefined,
                  },
                  pendingTextBox,
                };
              } else {
                llmState = {
                  type: "running",
                  mode: "prompt",
                  request: {
                    model: llmConfig.model,
                    prompt: inputValueBox.get(),
                    suffix: undefined,
                    max_tokens: llmConfig.max_tokens,
                    temperature: llmConfig.temperature,
                    top_p: undefined,
                    n: undefined,
                    stream: true,
                    logprobs: undefined,
                    echo: undefined,
                    stop: llmConfig.stop,
                    presence_penalty: undefined,
                    frequency_penalty: undefined,
                    best_of: undefined,
                    logit_bias: undefined,
                  },
                  pendingTextBox,
                };
              }
              const request = llmState.request;
              runInAction(() => {
                llmStateBox.set(llmState);
              });
              try {
                const response = await fetch(
                  `https://api.openai.com/v1/${
                    isLLMConfigChat(llmConfig) ? "chat/" : ""
                  }completions`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${apiKey[1]}`,
                    },
                    body: JSON.stringify(request),
                  }
                );
                if (response.body === null) {
                  return;
                }
                const reader = response.body
                  .pipeThrough(new TextDecoderStream())
                  .getReader();
                let fullResponse: any | undefined;
                while (true) {
                  const { value, done } = await reader.read();
                  if (done) {
                    break;
                  }
                  for (const line of value.split("\n")) {
                    if (line.startsWith("data: ")) {
                      const content = line.substring("data: ".length);
                      if (content !== "[DONE]") {
                        if (fullResponse === undefined) {
                          fullResponse = JSON.parse(content);
                          for (const choice of fullResponse.choices) {
                            delete choice.delta;
                          }
                        }
                        const json = JSON.parse(content);
                        for (
                          let choiceIndex = 0;
                          choiceIndex < json.choices.length;
                          choiceIndex++
                        ) {
                          const choice = json.choices[choiceIndex];
                          for (const [key, value] of Object.entries(choice)) {
                            if (key === "delta") {
                              for (const [
                                deltaKey,
                                deltaValue,
                              ] of Object.entries(value as any)) {
                                if (
                                  !(
                                    "message" in
                                    fullResponse.choices[choiceIndex]
                                  )
                                ) {
                                  fullResponse.choices[choiceIndex].message =
                                    {};
                                }
                                if (
                                  !(
                                    deltaKey in
                                    fullResponse.choices[choiceIndex].message
                                  )
                                ) {
                                  fullResponse.choices[choiceIndex].message[
                                    deltaKey
                                  ] = deltaValue;
                                } else {
                                  fullResponse.choices[choiceIndex].message[
                                    deltaKey
                                  ] += deltaValue;
                                }
                                if (
                                  choiceIndex === 0 &&
                                  deltaKey === "content"
                                ) {
                                  runInAction(() => {
                                    pendingTextBox.set(
                                      fullResponse.choices[0].message.content
                                    );
                                  });
                                }
                              }
                            } else if (key === "text") {
                              fullResponse.choices[choiceIndex].text += value;
                              if (choiceIndex === 0) {
                                runInAction(() => {
                                  pendingTextBox.set(
                                    fullResponse.choices[0].text
                                  );
                                });
                              }
                            } else {
                              fullResponse.choices[choiceIndex][key] = value;
                            }
                          }
                        }
                      }
                    }
                  }
                }

                if (response.status === 200) {
                  const json = fullResponse;
                  const llmState = llmStateBox.get();
                  if (
                    llmState.type === "running" &&
                    llmState.request === request
                  ) {
                    runInAction(() => {
                      llmStateBox.set({
                        type: "value",
                        // @ts-ignore
                        mode: llmState.mode,
                        // @ts-ignore
                        request: llmState.request,
                        response: json,
                      });
                    });
                  }
                } else {
                  const json = await response.json();
                  llmStateBox.set({
                    ...llmState,
                    type: "error",
                    error: json.error.message,
                  });
                }
              } catch (e: any) {
                runInAction(() => {
                  llmStateBox.set({
                    ...llmState,
                    type: "error",
                    error: e,
                  });
                });
              }
            }}
            className="button-dark-color px-2 py-1 flex items-center gap-1 rounded-sm hover:text-neutral-0 disabled:opacity-50 transition"
            disabled={apiKey[0] !== "value" || !isInputValueValid || isRunning}
          >
            {isRunning ? (
              "submitting..."
            ) : (
              <>
                <span>submit</span>
                <FeatherIcon icon="play" size="xs" />
              </>
            )}
          </button>
        </Tooltip>
        <div className="grow" />
        {tokensUsed !== undefined ? (
          <Tooltip
            content={
              <div>
                <div>{tokensUsed[0]} in prompt</div>
                <div>{tokensUsed[1]} in response</div>
              </div>
            }
          >
            <span className="text-neutral-400">{tokensUsed[2]} tokens</span>
          </Tooltip>
        ) : null}
      </div>
    );
  }
);

const JSON_RENDER_KEY_PATH_PREFIX = ["response"];

const LLMCompletionOutputValue = observer(
  ({
    paneId,
    inputValueBox,
    llmState,
  }: {
    paneId: string;
    inputValueBox: IComputedValue<any[] | undefined>;
    llmState: Extract<
      LLMPaneState,
      { type: "value" | "running"; mode: "prompt" }
    >;
  }) => {
    const [tabToShow, setTabToShow] = useState<"text" | "response">("text");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const hasPromptChanged = computed(() => {
      const inputValue = inputValueBox.get();
      return inputValue !== undefined && llmState.request.prompt !== inputValue;
    }).get();

    if (llmState.type === "running") {
      console.log("pending", llmState.pendingTextBox.get());
    }

    return (
      <div className="grow overflow-hidden relative group">
        <div
          className={classNames(
            "h-full overflow-auto select-text transition",
            hasPromptChanged ? "opacity-50" : undefined
          )}
        >
          {tabToShow === "text" ? (
            <div className="whitespace-pre-wrap p-2">
              <span>{llmState.request.prompt}</span>
              {llmState.type === "value" ? (
                <span className="bg-primary-100 dark:bg-primary-200">
                  {llmState.response.choices[0].text}
                </span>
              ) : llmState.type === "running" ? (
                <span className="bg-primary-100 dark:bg-primary-200">
                  {llmState.pendingTextBox.get()}
                </span>
              ) : null}
            </div>
          ) : llmState.type === "value" ? (
            <div className="p-2">
              <JSONRenderRoot
                paneId={paneId}
                value={llmState.response}
                keyPathPrefix={JSON_RENDER_KEY_PATH_PREFIX}
              />
            </div>
          ) : null}
        </div>
        <DropdownMenuRoot
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
          }}
        >
          <DropdownMenuTrigger asChild={true}>
            <button
              className={classNames(
                "absolute top-2 right-2 flex p-0.5 rounded-sm transition",
                isDropdownOpen
                  ? "bg-neutral-400 text-neutral-0"
                  : "bg-neutral-100 opacity-0 group-hover:opacity-100"
              )}
            >
              <FeatherIcon icon="more-horizontal" size="sm" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent sideOffset={4} align="end">
              {tabToShow === "text" ? (
                <DropdownMenuItem
                  onClick={() => {
                    setTabToShow("response");
                  }}
                >
                  view response
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => {
                    setTabToShow("text");
                  }}
                >
                  view text
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenuRoot>
      </div>
    );
  }
);

function ChatMessage({
  message,
  isResponse = false,
}: {
  message: ChatCompletionRequestMessage | ChatCompletionResponseMessage;
  isResponse?: boolean;
}) {
  return (
    <div className="flex items-start mx-2 py-1 border-b border-neutral-100 last-of-type:border-none">
      <div
        className={classNames(
          "w-[4.25rem] font-mono-natto text-2xs text-neutral-300 pt-px shrink-0 uppercase",
          isResponse ? "text-primary-500" : undefined
        )}
      >
        {message.role}
      </div>
      <div className="grow whitespace-pre-wrap">
        {isResponse ? (
          <span className="bg-primary-100 dark:bg-primary-200">
            {message.content.trim()}
          </span>
        ) : (
          message.content.trim()
        )}
      </div>
    </div>
  );
}

const LLMChatCompletionOutputValue = observer(
  ({
    paneId,
    inputValueBox,
    llmState,
  }: {
    paneId: string;
    inputValueBox: IComputedValue<any[] | undefined>;
    llmState: Extract<
      LLMPaneState,
      { type: "value" | "running"; mode: "chat" }
    >;
  }) => {
    const [tabToShow, setTabToShow] = useState<"messages" | "response">(
      "messages"
    );
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const hasPromptChanged = computed(() => {
      const inputValue = inputValueBox.get();
      return (
        inputValue !== undefined && llmState.request.messages !== inputValue
      );
    }).get();

    return (
      <div className="grow overflow-hidden relative group">
        <div
          className={classNames(
            "h-full overflow-auto select-text transition",
            hasPromptChanged ? "opacity-50" : undefined
          )}
        >
          {tabToShow === "messages" ? (
            <div className="py-2">
              {llmState.request.messages.map((message, i) => (
                <ChatMessage message={message} key={i} />
              ))}
              {llmState.type === "value" &&
              llmState.response.choices[0]?.message !== undefined ? (
                <ChatMessage
                  message={llmState.response.choices[0].message}
                  isResponse={true}
                />
              ) : llmState.type === "running" ? (
                <ChatMessage
                  message={{
                    role: "assistant",
                    content: llmState.pendingTextBox.get(),
                  }}
                  isResponse={true}
                />
              ) : null}
            </div>
          ) : llmState.type === "value" ? (
            <div className="p-2">
              <JSONRenderRoot
                paneId={paneId}
                value={llmState.response}
                keyPathPrefix={JSON_RENDER_KEY_PATH_PREFIX}
              />
            </div>
          ) : null}
        </div>
        <DropdownMenuRoot
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
          }}
        >
          <DropdownMenuTrigger asChild={true}>
            <button
              className={classNames(
                "absolute top-2 right-2 flex p-0.5 rounded-sm transition",
                isDropdownOpen
                  ? "bg-neutral-400 text-neutral-0"
                  : "bg-neutral-100 opacity-0 group-hover:opacity-100"
              )}
            >
              <FeatherIcon icon="more-horizontal" size="sm" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent sideOffset={4} align="end">
              {tabToShow === "messages" ? (
                <DropdownMenuItem
                  onClick={() => {
                    setTabToShow("response");
                  }}
                >
                  view response
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => {
                    setTabToShow("messages");
                  }}
                >
                  view messages
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenuRoot>
      </div>
    );
  }
);

const LLMOutput = observer(
  ({
    paneId,
    inputValueBox,
    llmStateBox,
    isChatMode,
    isInputValueValid,
  }: {
    paneId: string;
    inputValueBox: IComputedValue<any | undefined>;
    llmStateBox: IObservableValue<LLMPaneState>;
    isChatMode: boolean;
    isInputValueValid: boolean;
  }) => {
    const llmState = llmStateBox.get();

    if (llmState.type === "idle") {
      if (!isInputValueValid) {
        if (isChatMode) {
          return (
            <div className="p-2 text-neutral-400">
              <div className="pb-4">
                The messages input should have the following type.
              </div>
              <code className="whitespace-pre-wrap select-text">
                {`type Messages = Array<{
  'role': "system" | "user" | "assistant",
  'content': string,
}>`}
              </code>
            </div>
          );
        } else {
          return (
            <div className="p-2 text-neutral-400">
              The prompt input should be a string value.
            </div>
          );
        }
      }
      return null;
    }

    if (llmState.type === "error") {
      return (
        <div className="text-red-500 p-2 select-text">
          <div className="mb-4">something went wrong...</div>
          <div>{llmState.error?.toString?.()}</div>
        </div>
      );
    }

    if (llmState.mode === "chat") {
      return (
        <LLMChatCompletionOutputValue
          paneId={paneId}
          inputValueBox={inputValueBox}
          llmState={llmState}
        />
      );
    }

    return (
      <LLMCompletionOutputValue
        paneId={paneId}
        inputValueBox={inputValueBox}
        llmState={llmState}
      />
    );
  }
);

export const PaneLLM = observer(
  ({
    paneId,
    paneComputed,
    isMaximized,
    focusOnMount,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.LLM }> | undefined
    >;
    isMaximized: boolean;
    focusOnMount: boolean;
  }) => {
    const sessionApiKey = sessionOpenAIAPIKeyBox.get();
    const apiKey: EnvironmentVariableValue =
      sessionApiKey !== undefined
        ? ["value", sessionApiKey]
        : getEnvironmentVariableAtom(OPENAI_API_KEY_NAME).value;

    const activePaneState = activePaneStates.get(paneId)!;
    assert(activePaneState.type === PaneType.LLM);
    const outputBox = activePaneState.outputBoxes[0];
    const [llmStateBox] = useState(() =>
      observable.box<LLMPaneState>({ type: "idle" }, { deep: false })
    );
    useEffect(
      () =>
        reaction(
          () => llmStateBox.get(),
          action((llmState) => {
            switch (llmState.type) {
              case "running":
                outputBox.set(["running"]);
                break;
              case "value": {
                const choice = llmState.response.choices[0];
                outputBox.set([
                  "value",
                  {
                    text:
                      "text" in choice
                        ? choice.text
                        : "message" in choice
                        ? choice.message?.content
                        : undefined,
                    response: llmState.response,
                  },
                ]);
                break;
              }
              case "error":
                outputBox.set(["error", llmState.error]);
                break;
            }
          })
        ),
      []
    );

    const viewMode = activePaneState.viewMode.get();

    const isChatMode = computed(() => {
      const pane = paneComputed.get();
      return pane !== undefined && isLLMConfigChat(pane.config);
    }).get();
    const paneInputs = activePaneState.paneInputsComputed.get();
    const [showPopupPaneInputId, setShowPopupPaneInputId] = useState<
      PopupPaneInputId | undefined
    >(undefined);

    const inputValueBox = computed(() => {
      const pane = paneComputed.get();
      if (pane?.inputSource === undefined) {
        return undefined;
      }
      const [inputPaneId, outputIndex] = pane.inputSource;
      const inputPaneOutputBox =
        activePaneStates.get(inputPaneId)?.outputBoxes[outputIndex];
      const paneOutput = inputPaneOutputBox?.get();
      if (paneOutput?.[0] === "value") {
        return paneOutput[1];
      }
      return undefined;
    });

    const isInputValueValid = computed(() => {
      const pane = paneComputed.get();
      if (pane === undefined) {
        return false;
      }
      return isLLMConfigChat(pane.config)
        ? isValidChatInput(inputValueBox.get())
        : typeof inputValueBox.get() === "string";
    }).get();

    return (
      <>
        <PaneHeaderBar
          paneId={paneId}
          paneComputed={paneComputed}
          isMaximized={isMaximized}
        >
          {viewMode === PaneViewMode.Normal ? (
            <button
              onClick={action(() => {
                showOpenAISetupDialogAtom.value = true;
              })}
              className={classNames(
                "flex items-center gap-1.5 transition ml-2",
                apiKey[0] !== "value"
                  ? "text-primary-500"
                  : "text-neutral-400 hover:text-neutral-600"
              )}
            >
              <FeatherIcon icon="settings" size="sm" />
              {apiKey[0] !== "value" ? (
                <span className="font-medium">setup required</span>
              ) : null}
            </button>
          ) : null}
          <div className="grow" />
          {!isMaximized ? (
            <PaneTransformButton paneId={paneId} outputIndex={0} />
          ) : null}
        </PaneHeaderBar>
        <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
          <PaneSplit
            paneId={paneId}
            paneType={PaneType.LLM}
            activePaneState={activePaneState}
            isMaximized={isMaximized}
            inputElement={
              <div>
                <div className="pt-2 mx-2 text-neutral-400 flex items-center text-2xs">
                  <FeatherIcon icon="tool" size="sm" className="mr-1.5" />
                  This pane is in development. The UI and API will change!
                </div>
                <LLMConfigForm paneId={paneId} paneComputed={paneComputed} />
              </div>
            }
            outputElement={
              <div className="h-full relative bg-neutral-0 flex flex-col">
                <LLMRunBar
                  paneComputed={paneComputed}
                  inputValueBox={inputValueBox}
                  llmStateBox={llmStateBox}
                  apiKey={apiKey}
                  isChatMode={isChatMode}
                  isInputValueValid={isInputValueValid}
                />
                <LLMOutput
                  paneId={paneId}
                  inputValueBox={inputValueBox}
                  llmStateBox={llmStateBox}
                  isChatMode={isChatMode}
                  isInputValueValid={isInputValueValid}
                />
              </div>
            }
          />
        </PaneBody>
        {viewMode === PaneViewMode.Normal ? (
          <div
            className={
              computed(() => keyboardTransformPaneIdBox.get() === paneId).get()
                ? "opacity-50"
                : undefined
            }
          >
            {paneInputs.map((paneInput, inputIndex) => (
              <PaneInputNode
                inputIndex={inputIndex}
                paneId={paneId}
                paneType={PaneType.LLM}
                paneInputs={paneInputs}
                paneInput={paneInput}
                showPopupPaneInputId={showPopupPaneInputId}
                setShowPopupPaneInputId={setShowPopupPaneInputId}
                hideIfNotActive={isCustomLayoutId(canvasLayoutIdAtom.value)}
                tooltipContent={
                  isChatMode
                    ? "connect an array\nto use as messages"
                    : "connect a string\nto use as prompt"
                }
                key={paneInput.id}
              />
            ))}
          </div>
        ) : null}
      </>
    );
  }
);
